import Quill from "./quilljsEditor";
import { useContext, useEffect, useState, useRef } from "react";
import {
  MetaforceContext,
  PageElementContext,
  TextLibraryContext,
  WebEditorContext,
} from "../../../../contexts";
import * as Constants from "../../../../utils/constants";
import _ from "lodash";
import { useQuill } from "../../../../hooks/useQuill";
import { useTextLibraryAutoComplete } from "../../../../hooks/useTextLibraryAutoComplete";
import "./index.css";
import { getTextForCode } from "../../../../api";
import DOMPurify from "dompurify";
import { useEditorFile } from "../../../../hooks/useEditorFile";
import { useDynamicTableRows } from "../../../../hooks/useDynamicTableRows";

const Block = Quill.import("blots/block");

export default function RichTextEditor({ editorText, disabled = false }) {
  // After undo/redo clone the whole editorFile, editorText in quill editor-change handler is stale. Thus it uses a ref instead.
  const editorTextRef = useRef(editorText);
  const quillId = `quill${editorTextRef.current.Id}`;
  const {
    editorDisabled,
    setLastUsedQuillId,
    lastUsedQuillId,
    lastUsedQuillIndexRef,
    currentLanguage,
    setSearchClickedCount,
    setSideBarState,
    quillRef,
    lastUsedQuillRef,
    formatMenuBar,
    setFormatMenuBar,
    editorFileRef,
    autoSaveEditorFile,
    undoRedoManager,
    undoRedoLoading,
    setActionMenuBar,
  } = useContext(WebEditorContext);
  const {
    setTextLibraryDialogOpen,
    setQuillBeforeTextLibraryDialogOpen,
    setSelectedEditorInsertPoint,
  } = useContext(TextLibraryContext);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const { startParameter } = useContext(MetaforceContext);
  const {
    getQuill,
    insertQuillText,
    alignLeft,
    alignRight,
    alignCenter,
    alignJustify,
    createListByKeyShortcut,
    increaseFontSize,
    decreaseFontSize,
    formatBackgroundColor,
    setBold,
    setItalic,
    setUnderline,
  } = useQuill();
  // const timeOutRef = useRef();
  const autoComplete = useTextLibraryAutoComplete();
  const formatAtMultiCharsSelectionBeforeTextChange = useRef();
  const selectionAtMultiCharsSelectionBeforeTextChange = useRef();
  const keyPressAtMultiCharsSelectionBeforeTextChange = useRef(false);
  const editorFileManager = useEditorFile();
  const dynamicTableRows = useDynamicTableRows();
  const { handleContextMenu: handlePageElementContextMenu } = useContext(PageElementContext) || {};

  const formatBeforeTextChange = useRef();
  const selectionBeforeTextChange = useRef();
  const keyDownBeforeTextChange = useRef(false);

  const suppressAutoSave = useRef(false);

  const getFontSizes = (currentFormats, currentContents) => {
    let fontSizes = [];

    if (Array.isArray(currentFormats.fs)) {
      fontSizes = currentFormats.fs;
    } else if (typeof currentFormats.fs === "string") {
      fontSizes.push(currentFormats.fs);
    } else if (
      currentContents &&
      currentContents.ops &&
      currentContents.ops.length > 0
    ) {
      fontSizes = currentContents.ops.filter((item) => item.attributes?.fs);
      fontSizes = fontSizes.map((item) => {
        return item.attributes.fs;
      });
    }
    fontSizes = fontSizes.map((fontSize) => {
      fontSize = fontSize.replace("pt", "");
      return fontSize;
    });

    return fontSizes;
  };

  const handleFontFamilyName = (name) => {
    let fontFamilyName = name;
    if (fontFamilyName) {
      if (fontFamilyName[0] === '"') {
        fontFamilyName = fontFamilyName.substring(1);
      }
      if (fontFamilyName[fontFamilyName.length - 1] === '"') {
        fontFamilyName = fontFamilyName.substring(0, fontFamilyName.length - 1);
      }
    }
    if (!fontFamilyName) {
      fontFamilyName = "";
    }
    return fontFamilyName;
  };

  const getFontFamilies = (currentFormats, currentContents) => {
    let fontfamilies = [];

    if (Array.isArray(currentFormats.fontfamily)) {
      fontfamilies = currentFormats.fontfamily;
    } else if (typeof currentFormats.fontfamily === "string") {
      fontfamilies.push(currentFormats.fontfamily);
    } else if (
      currentContents &&
      currentContents.ops &&
      currentContents.ops.length > 0
    ) {
      fontfamilies = currentContents.ops.filter(
        (item) => item.attributes?.fontfamily,
      );
      fontfamilies = fontfamilies.map((item) => {
        return item.attributes.fontfamily;
      });
    }
    fontfamilies = fontfamilies.map((fontfamily) => {
      fontfamily = handleFontFamilyName(fontfamily);
      return fontfamily;
    });

    return fontfamilies;
  };

  const getFontFamilyStyles = (typeface, fontFamily) => {
    let styles = "";
    if (fontFamily) {
      if (typeface) {
        if (typeface.HasNormalFont === true) {
          styles += "Normal, ";
        }
        if (typeface.HasBoldFont === true) {
          styles += "Bold, ";
        }
        if (typeface.HasItalicFont === true) {
          styles += "Italic, ";
        }
        if (typeface.HasBoldItalicFont === true) {
          styles += "Bold Italic";
        }
      }
    }
    if (styles.substring(styles.length - 2) === ", ") {
      styles = styles.substring(0, styles.length - 2);
    }
    return styles;
  };

  const handleBoldItalicButton = (
    typeface,
    fontFamily,
    opsItem,
    currentFormats,
  ) => {
    let boldButtonDisabled = false;
    let italicButtonDisabled = false;
    let boldButtonMessage = "";
    let italicButtonMessage = "";

    let hasBold = currentFormats && currentFormats.bold ? true : false;
    if (opsItem) {
      hasBold = opsItem.attributes?.bold === true ? true : false;
    }
    let hasItalic = currentFormats && currentFormats.italic ? true : false;
    if (opsItem) {
      hasItalic = opsItem.attributes?.italic === true ? true : false;
    }

    if (typeface) {
      if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === false && hasItalic === false) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold style.\n`;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Italic style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === true && hasItalic === false) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Italic and Bold Italic style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === false && hasItalic === true) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold and Bold Italic style.\n`;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal and Italic style.\n`;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Normal and Bold style.\n`;
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === true && hasItalic === false) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Bold Italic style.\n`;
        } else if (hasBold === false && hasItalic === false) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Italic style.\n`;
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === false && hasItalic === true) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold Italic style.\n`;
        } else if (hasBold === false && hasItalic === false) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold style.\n`;
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Italic style.\n`;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Bold style.\n`;
        } else if (hasBold === false && hasItalic === false) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold style.\n`;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Italic style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === true && hasItalic === false) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Bold Italic style.\n`;
        } else if (hasBold === false && hasItalic === true) {
          boldButtonDisabled = true;
          italicButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold Italic style.\n`;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Italic style.\n`;
        } else if (hasBold === true && hasItalic === false) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Bold style.\n`;
        } else if (hasBold === false && hasItalic === true) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === false
      ) {
        if (hasBold === true && hasItalic === false) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Bold Italic style.\n`;
        } else if (hasBold === false && hasItalic === true) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold Italic style.\n`;
        } else if (hasBold === false && hasItalic === false) {
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === false &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Italic style.\n`;
        } else if (hasBold === true && hasItalic === false) {
        } else if (hasBold === false && hasItalic === false) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not make text '${opsItem?.insert}' tw-italic because font family '${fontFamily}' does not have Italic style.\n`;
        }
      } else if (
        typeface.HasNormalFont === true &&
        typeface.HasBoldFont === false &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Bold style.\n`;
        } else if (hasBold === false && hasItalic === true) {
        } else if (hasBold === false && hasItalic === false) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not make text '${opsItem?.insert}' bold because font family '${fontFamily}' does not have Bold style.\n`;
        }
      } else if (
        typeface.HasNormalFont === false &&
        typeface.HasBoldFont === true &&
        typeface.HasItalicFont === true &&
        typeface.HasBoldItalicFont === true
      ) {
        if (hasBold === true && hasItalic === true) {
        } else if (hasBold === true && hasItalic === false) {
          boldButtonDisabled = true;
          boldButtonMessage = `It can not unbold text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        } else if (hasBold === false && hasItalic === true) {
          italicButtonDisabled = true;
          italicButtonMessage = `It can not unitalicize text '${opsItem?.insert}' because font family '${fontFamily}' does not have Normal style.\n`;
        }
      }
    }

    let result = {
      boldButtonDisabled,
      boldButtonMessage,
      italicButtonDisabled,
      italicButtonMessage,
    };
    return result;
  };

  const checkTextLibraryDialogOpen = (quill, selection) => {
    if (selection?.index > 1) {
      const text = quill.getText(selection.index - 2, 2);
      if (
        text === Constants.textLibrarySearchKeywords &&
        editorFileRef.current.WebEditorSettings.TextLibraryEnabled
      ) {
        setTextLibraryDialogOpen(true);
        setQuillBeforeTextLibraryDialogOpen({
          id: quillId,
          index: selection.index - 2,
        });
      }
    }
  };

  const handleChange = async (quill, eventName, args) => {
    const selection = quill.getSelection();

    if (quill.root.contentEditable === "false") {
      quillRef.current = null;
      // disable toolbar
      setFormatMenuBar((prevState) => ({
        ...prevState,
        boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
        italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
        underlineButtonState: Constants.formatButtonState.disabledAndUnfocused,
        alignLeftButtonState: Constants.formatButtonState.disabledAndUnfocused,
        alignRightButtonState: Constants.formatButtonState.disabledAndUnfocused,
        alignCenterButtonState:
          Constants.formatButtonState.disabledAndUnfocused,
        alignJustifyButtonState:
          Constants.formatButtonState.disabledAndUnfocused,
        listButtonState: Constants.formatButtonState.disabledAndUnfocused,
        numberedListButtonState:
          Constants.formatButtonState.disabledAndUnfocused,
        fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
        fontFamilyButtonState: Constants.formatButtonState.disabledAndUnfocused,
      }));
    } else if (!selection) {
      if (
        !quillRef.current ||
        quill.container?.id === quillRef.current.container?.id
      ) {
        quillRef.current = null;

        // disable toolbar
        setFormatMenuBar((prevState) => ({
          ...prevState,
          boldButtonState: Constants.formatButtonState.disabledAndUnfocused,
          italicButtonState: Constants.formatButtonState.disabledAndUnfocused,
          underlineButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          alignLeftButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          alignRightButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          alignCenterButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          alignJustifyButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          listButtonState: Constants.formatButtonState.disabledAndUnfocused,
          numberedListButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
          fontSizeButtonState: Constants.formatButtonState.disabledAndUnfocused,
          fontFamilyButtonState:
            Constants.formatButtonState.disabledAndUnfocused,
        }));
      }
    } else {
      quillRef.current = quill;
      lastUsedQuillRef.current = quill;
      lastUsedQuillIndexRef.current = selection.index;
      setLastUsedQuillId(quillId);
      setSelectedEditorInsertPoint(null);

      // show toolbar
      const currentFormats = quill.getFormat();
      const currentContents = quill.getContents(
        selection.index,
        selection.length,
      );

      const newFormatMenuBar = _.cloneDeep(formatMenuBar);

      if (currentFormats.bold) {
        newFormatMenuBar.boldButtonState =
          Constants.formatButtonState.enabledAndFocused;
      } else {
        newFormatMenuBar.boldButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
      }

      if (currentFormats.italic) {
        newFormatMenuBar.italicButtonState =
          Constants.formatButtonState.enabledAndFocused;
      } else {
        newFormatMenuBar.italicButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
      }

      if (currentFormats.underline) {
        newFormatMenuBar.underlineButtonState =
          Constants.formatButtonState.enabledAndFocused;
      } else {
        newFormatMenuBar.underlineButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
      }

      if (
        currentFormats.list === Constants.quillFormatKeywords.bullet ||
        currentFormats.list === Constants.quillFormatKeywords.ordered
      ) {
        newFormatMenuBar.alignLeftButtonState =
          Constants.formatButtonState.disabledAndUnfocused;
        newFormatMenuBar.alignCenterButtonState =
          Constants.formatButtonState.disabledAndUnfocused;
        newFormatMenuBar.alignRightButtonState =
          Constants.formatButtonState.disabledAndUnfocused;
        newFormatMenuBar.alignJustifyButtonState =
          Constants.formatButtonState.disabledAndUnfocused;
      } else {
        if (!currentFormats.align) {
          newFormatMenuBar.alignLeftButtonState =
            Constants.formatButtonState.enabledAndFocused;
          newFormatMenuBar.alignCenterButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignRightButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignJustifyButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
        } else if (
          currentFormats.align === Constants.quillFormatKeywords.alignLeft
        ) {
          newFormatMenuBar.alignLeftButtonState =
            Constants.formatButtonState.enabledAndFocused;
          newFormatMenuBar.alignCenterButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignRightButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignJustifyButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
        } else if (
          currentFormats.align === Constants.quillFormatKeywords.alignRight
        ) {
          newFormatMenuBar.alignLeftButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignCenterButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignRightButtonState =
            Constants.formatButtonState.enabledAndFocused;
          newFormatMenuBar.alignJustifyButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
        } else if (
          currentFormats.align === Constants.quillFormatKeywords.alignCenter
        ) {
          newFormatMenuBar.alignLeftButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignCenterButtonState =
            Constants.formatButtonState.enabledAndFocused;
          newFormatMenuBar.alignRightButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignJustifyButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
        } else if (
          currentFormats.align === Constants.quillFormatKeywords.alignJustify
        ) {
          newFormatMenuBar.alignLeftButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignCenterButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignRightButtonState =
            Constants.formatButtonState.enabledAndUnfocused;
          newFormatMenuBar.alignJustifyButtonState =
            Constants.formatButtonState.enabledAndFocused;
        }
      }

      if (!currentFormats.list) {
        newFormatMenuBar.listButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
        newFormatMenuBar.numberedListButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
      } else if (currentFormats.list === Constants.quillFormatKeywords.bullet) {
        newFormatMenuBar.listButtonState =
          Constants.formatButtonState.enabledAndFocused;
        newFormatMenuBar.numberedListButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
      } else if (
        currentFormats.list === Constants.quillFormatKeywords.ordered
      ) {
        newFormatMenuBar.listButtonState =
          Constants.formatButtonState.enabledAndUnfocused;
        newFormatMenuBar.numberedListButtonState =
          Constants.formatButtonState.enabledAndFocused;
      }

      newFormatMenuBar.fontSizeButtonState =
        Constants.formatButtonState.enabledAndUnfocused;
      const fontSizes = getFontSizes(currentFormats, currentContents);
      if (fontSizes && fontSizes.length > 0) {
        newFormatMenuBar.fontSizeButtonValue = fontSizes[0];
      }

      newFormatMenuBar.fontFamilyButtonState =
        Constants.formatButtonState.enabledAndUnfocused;

      const fontFamilies = getFontFamilies(currentFormats, currentContents);
      if (fontFamilies && fontFamilies.length > 0) {
        newFormatMenuBar.fontFamilyButtonText = handleFontFamilyName(
          fontFamilies[0],
        );
      }

      //Handle Bold and Italic button according to typefaces of Fonts.conf
      if (selection) {
        let boldButtonDisabled = false;
        let italicButtonDisabled = false;
        let boldButtonMessage = "";
        let italicButtonMessage = "";

        if (selection.length === 0 && fontFamilies && fontFamilies.length > 0) {
          const fontFamily = handleFontFamilyName(fontFamilies[0]);

          const typeface = editorFileRef.current.EditorFontLibrary?.Fonts.find(
            (x) => x.Name === fontFamily,
          )?.Typeface;

          if (typeface) {
            const fontFamilyStyles = getFontFamilyStyles(typeface, fontFamily);

            let result = handleBoldItalicButton(
              typeface,
              fontFamily,
              null,
              currentFormats,
            );

            boldButtonDisabled = result.boldButtonDisabled
              ? result.boldButtonDisabled
              : boldButtonDisabled;
            italicButtonDisabled = result.italicButtonDisabled
              ? result.italicButtonDisabled
              : italicButtonDisabled;
            boldButtonMessage += result.boldButtonMessage;
            italicButtonMessage += result.italicButtonMessage;
          }
        } else {
          for (let i = 0; i < currentContents.ops.length; i++) {
            let opsItem = currentContents.ops[i];
            if (opsItem.insert === "\n") {
              continue;
            }
            if (!opsItem.attributes?.fontfamily) {
              boldButtonDisabled = true;
              italicButtonDisabled = true;
              boldButtonMessage += `Text '${opsItem.insert}' does not have font family.\n`;
              italicButtonMessage += `Text '${opsItem.insert}' does not have font family.\n`;
              continue;
            }

            const fontFamily = handleFontFamilyName(
              opsItem.attributes?.fontfamily,
            );

            const typeface =
              editorFileRef.current.EditorFontLibrary?.Fonts.find(
                (x) => x.Name === fontFamily,
              )?.Typeface;

            if (typeface) {
              const fontFamilyStyles = getFontFamilyStyles(
                typeface,
                fontFamily,
              );

              let result = handleBoldItalicButton(
                typeface,
                fontFamily,
                opsItem,
                null,
              );

              boldButtonDisabled = result.boldButtonDisabled
                ? result.boldButtonDisabled
                : boldButtonDisabled;
              italicButtonDisabled = result.italicButtonDisabled
                ? result.italicButtonDisabled
                : italicButtonDisabled;
              boldButtonMessage += result.boldButtonMessage;
              italicButtonMessage += result.italicButtonMessage;
            }
          }
        }

        if (boldButtonDisabled) {
          if (currentFormats.bold) {
            newFormatMenuBar.boldButtonState =
              Constants.formatButtonState.disabledAndFocused;
          } else {
            newFormatMenuBar.boldButtonState =
              Constants.formatButtonState.disabledAndUnfocused;
          }
        } else {
          if (currentFormats.bold) {
            newFormatMenuBar.boldButtonState =
              Constants.formatButtonState.enabledAndFocused;
          } else {
            newFormatMenuBar.boldButtonState =
              Constants.formatButtonState.enabledAndUnfocused;
          }
        }
        if (italicButtonDisabled) {
          if (currentFormats.italic) {
            newFormatMenuBar.italicButtonState =
              Constants.formatButtonState.disabledAndFocused;
          } else {
            newFormatMenuBar.italicButtonState =
              Constants.formatButtonState.disabledAndUnfocused;
          }
        } else {
          if (currentFormats.italic) {
            newFormatMenuBar.italicButtonState =
              Constants.formatButtonState.enabledAndFocused;
          } else {
            newFormatMenuBar.italicButtonState =
              Constants.formatButtonState.enabledAndUnfocused;
          }
        }
      }

      if (eventName === "text-change") {
        const beforeEditor = _.cloneDeep(editorFileRef.current)
        const [delta, oldDelta, source] = args;
        const innerHtml = quill.root.innerHTML;
        editorTextRef.current.HtmlContent = quill.root.innerHTML;
        const selection = quill.getSelection();

        const [blot, value] = quill.getLine(selection.index);
        const content = quill.getContents(selection.index - value, value);
        const currentLineContent = (typeof content.ops[0]?.insert === 'string')
          ? content.ops[0].insert.replace("\ufeff", "")
          : "";

        if (
          autoComplete.lookForSearch(currentLineContent, (input) =>
            handleSearchDetected(quill, input),
          )
        ) {
        } else if (
          autoComplete.lookForAutoComplete(
            currentLineContent,
            (code) => handleAutoComplete(quill, blot, value, code),
            () => handleAutoCompleteDetected(quill, value),
          )
        ) {
        } else {
          // const lineValue = blot.value();
          // console.log(quill);

          // Save state AFTER text change is complete
          // if (undoRedoManager && editorFileRef.current) {
          //   // Update the current editor text content
          //   editorTextRef.current.HtmlContent = quill.root.innerHTML;

          //   undoRedoManager.saveState({
          //     editorFile: beforeEditor,
          //     timestamp: Date.now(),
          //     source: 'textChange'
          //   });

          //   // Update button states - will be handled by useEffect in BaseLayout
          // }
          handleDynamicTableRows(quill);

          if (!suppressAutoSave.current) {
            autoSaveEditorFile();
          }

          checkTextLibraryDialogOpen(quill, selection);
        }
      }

      setFormatMenuBar(newFormatMenuBar);
    }
  };

  const handleDynamicTableRows = (quill) => {
    const text = quill.getText();
    dynamicTableRows.addDynamicTableRowIfNeeded(
      editorFileRef.current,
      editorTextRef.current,
      text,
      editorFileManager,
    );
    dynamicTableRows.removeLastDynamicTableRowIfNeeded(
      editorFileRef.current,
      editorTextRef.current,
      text,
      editorFileManager,
    );
  };

  const handleAutoCompleteDetected = (quill, length) => {
    const section = quill.getSelection();
    formatBackgroundColor(quill, section.index - length, length, "#fd5c63");
  };

  const handleSearchDetected = (quill, input) => {
    console.log("input", input);
    const section = quill.getSelection();
    lastUsedQuillIndexRef.current = section.index - input.length;

    quill.setSelection(section.index - input.length, input.length);
    const newSelection = quill.getSelection();

    quill.deleteText(newSelection.index, newSelection.length);

    // remove the red background color of the first character '\ufeff'
    if (newSelection.index >= 1) {
      const firstTextInLine = quill.getText(newSelection.index - 1, 1);
      if (firstTextInLine === "\ufeff") {
        quill.formatText(newSelection.index - 1, 1, "bgcolor", false);
      }
    }

    setTimeout(() => {
      setSideBarState(Constants.sideBarState.textLibrary);
      setSearchClickedCount((prevState) => prevState + 1);
    }, 50);
  };

  const handleAutoComplete = async (quill, blot, value, code) => {
    const codeExtractRegex = /[^# ]{1,}/gm;
    const matches = codeExtractRegex.exec(code);
    const cleanedCode = matches[0];
    console.log("cleanedCode", cleanedCode);
    const codeSearch = await getTextForCode(
      startParameter,
      cleanedCode,
      currentLanguage,
    );
    console.log(codeSearch);
    let textInsert = "No result for code";

    if (codeSearch) {
      textInsert = codeSearch.Text;
    }

    const section = quill.getSelection();
    var contents = quill.getContents();

    quill.setSelection(section.index - value, value);
    const newSelection = quill.getSelection();

    quill.deleteText(newSelection.index, newSelection.length);
    quill?.clipboard.dangerouslyPasteHTML(
      newSelection.index,
      `<br />${textInsert}`,
    );
    // quill.da(newSelection.index, textInsert, "AutoComplete", true);
    if (!codeSearch) {
      formatBackgroundColor(
        quill,
        newSelection.index,
        textInsert.length,
        "#D9D9D9",
      );
    }
    // quill.formatText(newSelection.index, insertText.length, true);
    const format = quill.getFormat(newSelection.index, textInsert.length);

    console.log(format);
    //
    // quill.insertText(section.index - codeLength, insertText);
    // let delta = new Delta();
    // delta = delta.retain(section - codeLength);
    // delta = delta.delete(codeLength);
    // delta = delta.insert(insertText + );

    // quill.updateContents(delta);
    // console.log(blot, value);
    // var contents = quill.getContents();
    // console.log(contents)
  };

  const handleAutoDate = (htmlContent) => {
    htmlContent.ops?.forEach((content) => {
      if (
        content.insert?.hasOwnProperty(Constants.autoDate.quillEmbedBlotName)
      ) {
        // replace autoDate span value by autoDate format
        // when it sets quill content, it will create autoDateBlot and this format becomes the input of create()
        // <span auto-date-field='d/M/yyyy' contenteditable='false'></span>
        if (
          content.attributes?.hasOwnProperty(Constants.autoDate.htmlAttribute)
        ) {
          content.insert[Constants.autoDate.quillEmbedBlotName] =
            content.attributes[Constants.autoDate.htmlAttribute];
        } else {
          content.insert[Constants.autoDate.quillEmbedBlotName] = "";
        }
      }
    });
  };

  useEffect(() => {
    editorTextRef.current = editorText;
  }, [editorText]);

  useEffect(() => {
    let Clipboard = Quill.import("modules/clipboard");
    const Delta = Quill.import("delta");
    var Font = Quill.import("formats/font");
    // We do not add Aref Ruqaa since it is the default
    const editorFonts = editorFileRef.current.EditorFontLibrary?.Fonts.map(
      (x) => x.Typeface?.Name,
    ).filter((x) => !!x);

    Font.whitelist = editorFonts;
    Quill.register(Font, true);
    // console.log(Quill.imports)
    class PlainClipboard extends Clipboard {
      onPaste(e) {
        if (!quillRef.current && !quillRef.current.isEnabled()) {
          return;
        }

        // Note: Paste state saving is handled after paste operation completes

        e.preventDefault();

        const range = quillRef.current.getSelection();
        const keepSelection = false;
        let text;
        let html;
        let file;

        if (
          (!e.clipboardData || !e.clipboardData.getData) &&
          window.clipboardData &&
          window.clipboardData.getData
        ) {
          // compatibility with older IE versions
          text = window.clipboardData.getData("Text");
        } else {
          text = e.clipboardData.getData("text/plain");
          html = e.clipboardData?.types?.includes("text/html")
            ? e.clipboardData.getData("text/html")
            : text;
          file = e.clipboardData?.items?.[0];
        }

        this.handlePasteContent(quillRef.current, range, text, html, keepSelection);
      }

      handlePasteContent(currentQuill, range, text, html, keepSelection) {
        const substituteBlockElements = true;

        let delta = new Delta().retain(range.index).delete(range.length);
        let DOMPurifyOptions = this.getDOMPurifyOptions();
        let content = text;
        if (substituteBlockElements) {
          // html = DOMPurify.sanitize(html, { ...DOMPurifyOptions, ...{ RETURN_DOM: true, WHOLE_DOCUMENT: false } });
          html = this.substitute(html, DOMPurifyOptions);
          content = html.innerHTML;
        } else {
          content = DOMPurify.sanitize(html, DOMPurifyOptions);
        }
        //   delta = delta.concat(this.convert(content));

        // const currentFormats = quill.getFormat();
        // console.log(html);
        // debugger;
        // const typeface = editorFileRef.current.EditorFontLibrary?.Fonts[0]?.Typeface;
        // const currentContents = currentQuill.getContents();
        // let currentFonts = currentContents.map(x => x.attributes?.fontfamily).filter(font => !!font);
        // let fontFamily;
        // if (currentFonts == 0) {
        //     fontFamily = `"${typeface.Name}"`;
        // }
        // else if (currentFonts === 1) {
        //     fontFamily = currentFonts[0]
        // } else {
        //     let counts = currentFonts.reduce((prev, curr, i) => {
        //         if (!prev[curr]) {
        //             prev[curr] = 0
        //         }
        //         prev[curr]++
        //         return prev;
        //     }, {});
        //     counts = Object.keys(counts).map(k => ({ [k]: counts[k] }));

        //     const sorted = counts.sort((a, b) => {
        //         const fontA = Object.keys(a)[0];
        //         const fontB = Object.keys(b)[0];
        //         if (a[fontA] < b[fontB]) {
        //             return 1;
        //         }
        //         if (a[fontA] >= b[fontB]) {
        //             return -1;
        //         }

        //     })
        //     const maxCountFont = sorted[0];
        //     const fontName = Object.keys(maxCountFont)[0];

        //     fontFamily = `${fontName}`;
        // }

        //debugger;
        const converted = this.convert(content);
        delta = delta.concat(converted);

        currentQuill.updateContents(delta, Quill.sources.USER);

        if (keepSelection)
          currentQuill.setSelection(
            range.index,
            converted.length(),
            Quill.sources.SILENT,
          );
        else
          currentQuill.setSelection(
            range.index + converted.length(),
            0,
            Quill.sources.SILENT,
          );
        currentQuill.scrollIntoView();

        // Save state after paste operation completes
        // setTimeout(() => {
        //   if (undoRedoManager && editorFileRef.current) {
        //     // Update the current editor text content
        //     editorTextRef.current.HtmlContent = currentQuill.root.innerHTML;

        //     undoRedoManager.saveState({
        //       editorFile: _.cloneDeep(editorFileRef.current),
        //       timestamp: Date.now(),
        //       source: 'paste'
        //     });

        //     // Update button states - will be handled by useEffect in BaseLayout
        //   }
        // }, 50);

        //old linlin
        // let selection = currentQuill.getSelection();
        // if (selection) {
        //     if (selection.length > 0) {
        //         currentQuill.deleteText(selection.index, selection.length);
        //     }

        //     // paste plain text or html formatted text from TextLibrary
        //     const text = e.clipboardData.getData('text');

        //     insertQuillText(currentQuill, text, selection.index);
        // }
        // const newContents = currentQuill.getContents();
      }

      substitute(html, DOMPurifyOptions) {
        let substitution;

        const headings = ["h1", "h2", "h3", "h4", "h5", "h6"];
        const blockElements = [
          "p",
          "div",
          "section",
          "article",
          "fieldset",
          "address",
          "aside",
          "blockquote",
          "canvas",
          "dl",
          "figcaption",
          "figure",
          "footer",
          "form",
          "header",
          "main",
          "nav",
          "noscript",
          "ol",
          "pre",
          "table",
          "tfoot",
          "ul",
          "video",
        ];
        const newLineElements = ["li", "dt", "dd", "hr"];

        DOMPurify.addHook("uponSanitizeElement", (node, data, config) => {
          // check if current tag is a heading
          // - is it supported?
          // - no? - replace it with <p> and <b>
          // -----------------
          // check if current tag is a block element
          // - is it supported?
          // - no? - replace it with <p>
          // -----------------
          // check if current tag is a new line element
          // - is it supported?
          // - no? - remove the tag and append a <br>

          // find possible substitution
          let i = 0;
          while (!substitution && i < 3) {
            if (DOMPurifyOptions.ALLOWED_TAGS.includes(blockElements[i]))
              substitution = blockElements[i];
            ++i;
          }

          if (
            substitution &&
            node.tagName &&
            !DOMPurifyOptions.ALLOWED_TAGS.includes(node.tagName.toLowerCase())
          ) {
            const tagName = node.tagName.toLowerCase();
            if (headings.includes(tagName)) {
              node.innerHTML = `<${substitution}><b>${node.innerHTML}</b></${substitution}>`;
            } else if (blockElements.includes(tagName)) {
              node.innerHTML = `<${substitution}>${node.innerHTML}</${substitution}>`;
            } else if (newLineElements.includes(tagName)) {
              node.innerHTML = `${node.innerHTML}<br>`;
            }
          }
        });

        html = DOMPurify.sanitize(html, {
          ...DOMPurifyOptions,
          ...{ RETURN_DOM: true, WHOLE_DOCUMENT: false },
        });
        DOMPurify.removeAllHooks();

        // fix quill bug #3333
        // span content placed into the next tag

        const createElement = (node) => {
          const element = document.createElement(node.tagName.toLowerCase());
          const attributes = node.attributes;
          if (attributes.length) {
            Array.from(attributes).forEach((el) =>
              element.setAttribute(el.nodeName, el.value),
            );
          }
          return element;
        };

        let depth = 0;
        const walkTheDOM = (node, func) => {
          func(node, depth);
          // node = node.firstChild;
          if (depth <= 1) node = node.firstChild;
          else node = undefined;
          while (node) {
            ++depth;
            walkTheDOM(node, func);
            node = node.nextSibling;
          }
          --depth;
        };

        let block;
        const fixedDom = document.createElement("body");
        walkTheDOM(html, (node, depth) => {
          if (depth === 1) {
            if (
              node.tagName &&
              blockElements.includes(node.tagName.toLowerCase())
            ) {
              if (block) block = undefined;
              const element = createElement(node);

              element.innerHTML = node.innerHTML;
              fixedDom.appendChild(element);
            } else {
              if (block === undefined) {
                block = document.createElement(substitution);
                fixedDom.appendChild(block);
              }

              if (node.tagName) {
                const element = createElement(node);

                if (node.innerHTML) element.innerHTML = node.innerHTML;
                block.appendChild(element);
              } else {
                // plain text
                const element = document.createTextNode(node.textContent);
                block.appendChild(element);
              }
            }
          }
        });

        return fixedDom;
      }

      getDOMPurifyOptions() {
        let tidy = {};
        // 'h1', 'h2', 'h2', 'h3', 'h4', 'h5', 'h6'
        let allowed = {
          tags: [
            "a",
            "b",
            "strong",
            "u",
            "s",
            "i",
            "p",
            "br",
            "ul",
            "ol",
            "li",
            "span",
          ],
          attributes: ["href", "rel", "target", "class"],
        };

        if (allowed?.tags) tidy.ALLOWED_TAGS = allowed.tags;
        if (allowed?.attributes) tidy.ALLOWED_ATTR = allowed.attributes;

        if (
          tidy.ALLOWED_TAGS === undefined ||
          tidy.ALLOWED_ATTR === undefined
        ) {
          let undefinedTags = false;
          if (tidy.ALLOWED_TAGS === undefined) {
            undefinedTags = true;
            tidy.ALLOWED_TAGS = ["p", "br", "span"];
          }

          let undefinedAttr = false;
          if (tidy.ALLOWED_ATTR === undefined) {
            undefinedAttr = true;
            tidy.ALLOWED_ATTR = ["class"];
          }

          const toolbar = quillRef.current.getModule("toolbar");
          toolbar?.controls?.forEach((control) => {
            switch (control[0]) {
              case "bold":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("b");
                  tidy.ALLOWED_TAGS.push("strong");
                }
                break;

              case "italic":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("i");
                  tidy.ALLOWED_TAGS.push("em");
                }
                break;

              case "underline":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("u");
                }
                break;

              case "strike":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("s");
                }
                break;

              case "color":
              case "background":
                if (undefinedAttr) {
                  tidy.ALLOWED_ATTR.push("style");
                }
                break;

              case "script":
                if (undefinedTags) {
                  if (control[1].value === "super") {
                    tidy.ALLOWED_TAGS.push("sup");
                  } else if (control[1].value === "sub") {
                    tidy.ALLOWED_TAGS.push("sub");
                  }
                }
                break;

              case "header":
                if (undefinedTags) {
                  const detectAllowedHeadingTag = (value) => {
                    if (value === "1") {
                      tidy.ALLOWED_TAGS.push("h1");
                    } else if (value === "2") {
                      tidy.ALLOWED_TAGS.push("h2");
                    } else if (value === "3") {
                      tidy.ALLOWED_TAGS.push("h3");
                    } else if (value === "4") {
                      tidy.ALLOWED_TAGS.push("h4");
                    } else if (value === "5") {
                      tidy.ALLOWED_TAGS.push("h5");
                    } else if (value === "6") {
                      tidy.ALLOWED_TAGS.push("h6");
                    }
                  };

                  if (control[1].value)
                    detectAllowedHeadingTag(control[1].value);
                  else if (control[1].options && control[1].options.length) {
                    [].forEach.call(control[1].options, (option) => {
                      if (option.value) detectAllowedHeadingTag(option.value);
                    });
                  }
                }
                break;

              case "code-block":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("pre");
                }
                if (undefinedAttr) {
                  tidy.ALLOWED_ATTR.push("spellcheck");
                }
                break;

              case "list":
                if (undefinedTags) {
                  if (control[1].value === "ordered") {
                    tidy.ALLOWED_TAGS.push("ol");
                  } else if (control[1].value === "bullet") {
                    tidy.ALLOWED_TAGS.push("ul");
                  }
                  tidy.ALLOWED_TAGS.push("li");
                }
                break;

              case "link":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("a");
                }
                if (undefinedAttr) {
                  tidy.ALLOWED_ATTR.push("href");
                  tidy.ALLOWED_ATTR.push("target");
                  tidy.ALLOWED_ATTR.push("rel");
                }
                break;

              case "image":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("img");
                }
                if (undefinedAttr) {
                  tidy.ALLOWED_ATTR.push("src");
                  tidy.ALLOWED_ATTR.push("title");
                  tidy.ALLOWED_ATTR.push("alt");
                }
                break;

              case "video":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push("iframe");
                }
                if (undefinedAttr) {
                  tidy.ALLOWED_ATTR.push("frameborder");
                  tidy.ALLOWED_ATTR.push("allowfullscreen");
                  tidy.ALLOWED_ATTR.push("src");
                }
                break;

              case "blockquote":
                if (undefinedTags) {
                  tidy.ALLOWED_TAGS.push(control[0]);
                }
                break;
            }
          });
        }

        return tidy;
      }
    }

    Quill.register("modules/clipboard", PlainClipboard, true);

    let keyboardBindings = {
      // overwrite quill js default key binding tab
      tab: {
        key: "Tab",
        handler: function (range, context) {
          return true;
        },
      },
      // overwrite quill js default key binding 'indent'
      indent: {
        // highlight tab or tab at beginning of list, indent or blockquote
        key: "Tab",
        format: ["blockquote", "indent", "list"],
        handler: function (range, context) {
          return true;
        },
      },
      // overwrite quill js default key binding 'outdent'
      outdent: {
        key: "Tab",
        shiftKey: true,
        format: ["blockquote", "indent", "list"],
        // highlight tab or tab at beginning of list, indent or blockquote
        handler: function (range, context) {
          return true;
        },
      },
      bold: {
        key: "b",
        shortKey: true,
        handler: function (range, context) {
          setBold(this.quill, setFormatMenuBar);
        },
      },
      italic: {
        key: "i",
        shortKey: true,
        handler: function (range, context) {
          setItalic(this.quill, setFormatMenuBar);
        },
      },
      underline: {
        key: "u",
        shortKey: true,
        handler: function (range, context) {
          setUnderline(this.quill, setFormatMenuBar);
        },
      },
      alignLeft: {
        key: "L",
        ctrlKey: true,
        handler: function (range, context) {
          alignLeft(this.quill);
        },
      },
      alignRight: {
        key: "R",
        ctrlKey: true,
        handler: function (range, context) {
          alignRight(this.quill);
        },
      },
      alignCenter: {
        key: "E",
        ctrlKey: true,
        handler: function (range, context) {
          alignCenter(this.quill);
        },
      },
      alignJustify: {
        key: "J",
        ctrlKey: true,
        handler: function (range, context) {
          alignJustify(this.quill);
        },
      },
      // overwrite quill js default key binding 'list autofill'
      "list autofill": {
        key: " ",
        collapsed: true,
        format: { list: false },
        prefix: /^\s*?(\d+\.|-|\*|\[ ?\]|\[x\])$/,
        handler: function (range, context) {
          createListByKeyShortcut(this.quill, range, context);
        },
      },
      decreaseFontSize: {
        key: "8",
        ctrlKey: true,
        shiftKey: true,
        handler: function (range, context) {
          decreaseFontSize(this.quill, setFormatMenuBar);
        },
      },
      increaseFontSize: {
        key: "9",
        ctrlKey: true,
        shiftKey: true,
        handler: function (range, context) {
          increaseFontSize(this.quill, setFormatMenuBar);
        },
      },
    };

    const editorElement = document.getElementById(quillId);
    let quill = new Quill(editorElement, {
      modules: {
        toolbar: false,
        keyboard: {
          bindings: keyboardBindings,
        },
      },
    });

    const htmlContent = quill.clipboard.convert(editorTextRef.current.HtmlContent);
    handleAutoDate(htmlContent);
    quill.setContents(htmlContent);

    quill.on("editor-change", async (eventName, ...args) => {
      if (editorDisabled || disabled) {
        quillRef.current = null;
      } else {
        handleChange(quill, eventName, args);
      }
    });

    setIsInitialLoad(true);

    if (!lastUsedQuillRef?.current) {
      lastUsedQuillRef.current = quill;
      setLastUsedQuillId(quillId);
    }
  }, [startParameter]);

  useEffect(() => {
    const quill = getQuill(quillId);
    if (quill) {
      if (editorDisabled || disabled) {
        quill.disable();
      } else {
        quill.enable();
      }
    }
  }, [editorDisabled, disabled]);

  // Save initial state when editor loads
  // useEffect(() => {
  //   if (isInitialLoad && undoRedoManager && editorFileRef.current) {
  //     setTimeout(() => {
  //       undoRedoManager.saveState({
  //         editorFile: _.cloneDeep(editorFileRef.current),
  //         timestamp: Date.now(),
  //         source: 'initialLoad'
  //       });

  //       // Update button states - will be handled by useEffect in BaseLayout
  //     }, 100); // Give editor time to fully initialize
  //   }
  // }, [isInitialLoad, undoRedoManager, editorFileRef]);

  useEffect(() => {
    // update quill content after undo/redo completes
    if (!isInitialLoad || undoRedoLoading)
      return;

    const quill = getQuill(quillId);
    if (!quill)
      return;

    suppressAutoSave.current = true;
    const htmlContent = quill.clipboard.convert(editorTextRef.current.HtmlContent);
    quill.setContents(htmlContent);

    // Quill setContents() will fire editor-change event and autoSave
    // Use suppressAutoSave to skip autoSave after undo/redo updates quill content  
    // Allow one event loop tick before re-enabling autosave
    setTimeout(() => {
      suppressAutoSave.current = false;
    }, 0);
  }, [undoRedoLoading])

  // Order of events is keyDown, keyPress, text-change (quill) and keyUp
  const handleKeyDown = (e) => {
    // if ctrl f is pushed
    if (
      e.ctrlKey &&
      e.key === "f" &&
      editorFileRef?.current?.WebEditorSettings?.TextLibraryEnabled
    ) {
      e.preventDefault();
      setSideBarState(Constants.sideBarState.textLibrary);
      setSearchClickedCount((prev) => prev + 1);
    }

    const quill = getQuill(quillId);
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    // Note: State saving is now handled in text-change event after the change is complete

    // keyDownBeforeTextChange is used to check if the user clicks keyboards quickly and continuelly
    // For example, click a b quickly. The events are keyDown(a), keyPress(a), keyDown(b), keyPress(b), textChange, keyUp(a), keyUp(b)
    if (!keyDownBeforeTextChange.current) {
      formatBeforeTextChange.current = quill.getFormat(selection.index, 0);
      selectionBeforeTextChange.current = _.cloneDeep(selection);
      keyDownBeforeTextChange.current = true;
    }
  };

  const handleKeyPress = (e) => {
    const quill = getQuill(quillId);
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    // multiple characters selection
    if (selection.length > 0) {
      // keyPressAtMultiCharsSelectionBeforeTextChange is used to check if the user clicks keyboards quickly and continuelly
      // For example, click a b quickly. The events are keyDown(a), keyPress(a), keyDown(b), keyPress(b), textChange, keyUp(a), keyUp(b)
      if (!keyPressAtMultiCharsSelectionBeforeTextChange.current) {
        formatAtMultiCharsSelectionBeforeTextChange.current = quill.getFormat(
          selection.index,
          0,
        );
        selectionAtMultiCharsSelectionBeforeTextChange.current =
          _.cloneDeep(selection);
        quill.deleteText(selection.index, selection.length);
        keyPressAtMultiCharsSelectionBeforeTextChange.current = true;
      }
    }
  };

  const handleKeyUp = (e) => {
    const quill = getQuill(quillId);
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;
    handleMultiCharsSelectionAfterTextChange(quill, selection);

    checkFormatAfterTextChange(quill, selection);
  };

  const handleMultiCharsSelectionAfterTextChange = (quill, selection) => {
    if (selectionAtMultiCharsSelectionBeforeTextChange.current) {
      const length =
        selection.index -
        selectionAtMultiCharsSelectionBeforeTextChange.current.index;
      if (length > 0) {
        if (formatAtMultiCharsSelectionBeforeTextChange.current) {
          quill.formatText(
            selectionAtMultiCharsSelectionBeforeTextChange.current.index,
            length,
            formatAtMultiCharsSelectionBeforeTextChange.current,
          );
          quill.formatLine(
            selectionAtMultiCharsSelectionBeforeTextChange.current.index,
            length,
            formatAtMultiCharsSelectionBeforeTextChange.current,
          );
        }
      }
    }

    formatAtMultiCharsSelectionBeforeTextChange.current = null;
    selectionAtMultiCharsSelectionBeforeTextChange.current = null;
    keyPressAtMultiCharsSelectionBeforeTextChange.current = false;
  };

  const getFontFamilyOfPreviousText = (quill, selection) => {
    let fontFamily = "";
    if (
      formatBeforeTextChange.current &&
      formatBeforeTextChange.current.fontfamily
    ) {
      fontFamily = formatBeforeTextChange.current.fontfamily;
    }

    if (!fontFamily) {
      const preContents = quill.getContents(0, selection.index);
      if (preContents && preContents.ops) {
        const lastItem = preContents.ops.findLast(
          (x) => x.attributes?.fontfamily,
        );
        if (lastItem) {
          fontFamily = lastItem.attributes?.fontfamily;
        }
      }
    }

    if (!fontFamily) {
      if (editorTextRef.current && editorTextRef.current.DefaultEditorFontDef) {
        fontFamily = editorTextRef.current.DefaultEditorFontDef.Name;
      }
    }

    return fontFamily;
  };

  const getFontSizeOfPreviousText = (quill, selection) => {
    let fontSize = "";
    if (formatBeforeTextChange.current && formatBeforeTextChange.current.fs) {
      fontSize = formatBeforeTextChange.current.fs;
    }

    if (!fontSize) {
      const preContents = quill.getContents(0, selection.index);
      if (preContents && preContents.ops) {
        const lastItem = preContents.ops.findLast((x) => x.attributes?.fs);
        if (lastItem) {
          fontSize = lastItem.attributes?.fs;
        }
      }
    }

    if (!fontSize) {
      if (editorTextRef.current && editorTextRef.current.DefaultEditorFontDef) {
        fontSize = `${editorTextRef.current.DefaultEditorFontDef.Size}pt`;
      }
    }

    return fontSize;
  };

  const getDefaultFormatsFromTypeface = (typeface) => {
    let formats = {};

    if (typeface.HasNormalFont) {
      formats[Constants.quillFormatKeywords.bold] = false;
      formats[Constants.quillFormatKeywords.italic] = false;
    } else if (typeface.HasBoldFont) {
      formats[Constants.quillFormatKeywords.bold] = true;
      formats[Constants.quillFormatKeywords.italic] = false;
    } else if (typeface.HasItalicFont) {
      formats[Constants.quillFormatKeywords.bold] = false;
      formats[Constants.quillFormatKeywords.italic] = true;
    } else if (typeface.HasBoldItalicFont) {
      formats[Constants.quillFormatKeywords.bold] = true;
      formats[Constants.quillFormatKeywords.italic] = true;
    }

    return formats;
  };

  const checkTypefaceAndGetFormat = (quill, currentFormats, fontFamily) => {
    let newFormats = {};

    // check typeface (fonts.conf) defined in the WebService
    if (fontFamily) {
      const name = handleFontFamilyName(fontFamily);
      const typeface = editorFileRef.current.EditorFontLibrary?.Fonts.find(
        (x) => x.Name === name,
      )?.Typeface;
      if (typeface) {
        if (!currentFormats.bold && !currentFormats.italic) {
          if (!typeface.HasNormalFont) {
            newFormats = getDefaultFormatsFromTypeface(typeface);
          }
        } else if (currentFormats.bold && !currentFormats.italic) {
          if (!typeface.HasBoldFont) {
            newFormats = getDefaultFormatsFromTypeface(typeface);
          }
        } else if (!currentFormats.bold && currentFormats.italic) {
          if (!typeface.HasItalicFont) {
            newFormats = getDefaultFormatsFromTypeface(typeface);
          }
        } else if (currentFormats.bold && currentFormats.italic) {
          if (!typeface.HasBoldItalicFont) {
            newFormats = getDefaultFormatsFromTypeface(typeface);
          }
        }
      }
    }

    return newFormats;
  };

  const checkFormatAfterTextChange = (quill, selection) => {
    if (selectionBeforeTextChange.current) {
      const length = selection.index - selectionBeforeTextChange.current.index;
      if (length > 0) {
        let newFormats = {};

        const currentFormats = quill.getFormat(
          selectionBeforeTextChange.current.index,
          length,
        );

        // check font family
        let fontFamily = currentFormats.fontfamily;
        if (!currentFormats.fontfamily) {
          fontFamily = getFontFamilyOfPreviousText(quill, selection);
          if (fontFamily) {
            newFormats[Constants.quillFormatKeywords.fontfamily] = fontFamily;
          }
        }

        // check font size
        if (!currentFormats.fs) {
          const fontSize = getFontSizeOfPreviousText(quill, selection);
          if (fontSize) {
            newFormats[Constants.quillFormatKeywords.fontSize] = fontSize;
          }
        }

        // check typeface
        let typefaceFormats = checkTypefaceAndGetFormat(
          quill,
          currentFormats,
          fontFamily,
        );
        if (!_.isEmpty(typefaceFormats)) {
          Object.assign(newFormats, typefaceFormats);
        }

        // update format
        if (!_.isEmpty(newFormats)) {
          quill.formatText(
            selectionBeforeTextChange.current.index,
            length,
            newFormats,
          );
        }
      }
    }

    formatBeforeTextChange.current = null;
    selectionBeforeTextChange.current = null;
    keyDownBeforeTextChange.current = false;
  };

  const handleContextMenu = (e) => {
    if (!handlePageElementContextMenu)
      return;

    e.preventDefault();

    let range;
    const quill = getQuill(quillId);
    if (quill) {
      range = quill.getSelection();
    }

    e.extraMenuItems = null;
    e.selectedElement = { type: editorTextRef.current.Type, elementRef: quill, range: range };
  }

  return (
    <div
      style={editorTextRef.current.ReactCssStyle}
      className={
        (lastUsedQuillId === quillId
          ? " tw-border tw-rounded-sm tw-shadow-sm tw-border-indigo-700"
          : "") + (editorDisabled || disabled ? "" : " tw-bg-white ")
      }
    >
      <div
        id={quillId}
        className={" tw-border tw-rounded-sm tw-shadow-sm tw-px-3 tw-py-3"}
        style={
          typeof editorTextRef.current.TextboxCssStyle === "object"
            ? editorTextRef.current.TextboxCssStyle
            : {}
        }
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        onKeyUp={handleKeyUp}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
}
